/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import {
    type FetchHiveContentAuthTokenCallback,
    type HiveAuthTokenCacheData,
    HiveClient,
    type HiveClientOptions,
    type HiveGlobalPresence,
} from '@gitbook/hive-client';
import type { ContentComputerFactory } from '@gitbook/hive-core';
import { useOnPageBecomeActive } from '@gitbook/util-react-hooks';
import type { SuspenceBrowserCache } from '@gitbook/util-suspense-cache';

import { HiveContext, HiveGlobalPresenceContext } from './HiveContext';

type HiveClientProviderProps =
    | {
          /**
           * Pre-initialized client.
           */
          client: HiveClient;

          presence?: HiveGlobalPresence;

          endpoint?: undefined;
          getAuthToken?: undefined;
          store?: undefined;
          compute?: undefined;
          isHiveV2?: undefined;
          tokensBrowserCache?: undefined;
      }
    | {
          client?: undefined;

          /**
           * URL of the Hive API.
           */
          endpoint: HiveClientOptions['endpoint'];

          /**
           * Callback that fetches auth tokens for content.
           */
          getAuthToken: FetchHiveContentAuthTokenCallback;

          /**
           * Global presence data.
           */
          presence?: HiveGlobalPresence;

          /**
           * Pre-loaded content store cache.
           */
          store?: object;

          /**
           * Factory to create a content computer.
           */
          compute?: ContentComputerFactory;

          /**
           * Optional resolver/value indicating if a content is using Hive V2 DataSync.
           */
          isHiveV2?: HiveClientOptions['isHiveV2'];

          /**
           * When used in the browser, pass a `SuspenceBrowserCache` to automatically
           * persist tokens in the browser's cache IndexedDB.
           */
          tokensBrowserCache?: SuspenceBrowserCache<HiveAuthTokenCacheData>;
      };

/**
 * Provider for the HiveClient.
 */
export function HiveProvider(props: React.PropsWithChildren<HiveClientProviderProps>) {
    const { presence, children } = props;

    const client = React.useMemo(() => {
        if (props.client) {
            return props.client;
        }

        const client = new HiveClient({
            endpoint: props.endpoint,
            getAuthToken: props.getAuthToken,
            tokensBrowserCache: props.tokensBrowserCache,
            compute: props.compute,
            isHiveV2: props.isHiveV2,
        });
        if (props.store) {
            client.store.fromJSON(props.store);
        }

        return client;
    }, [
        props.client,
        props.endpoint,
        props.getAuthToken,
        props.store,
        props.tokensBrowserCache,
        props.compute,
        props.isHiveV2,
    ]);

    const presenceValue = React.useMemo(() => {
        if (!presence?.userId) {
            return null;
        }

        return {
            userId: presence.userId,
            userDisplayName: presence.userDisplayName,
        };
    }, [presence?.userId, presence?.userDisplayName]);

    // Recover quickly when the user comes back to the page: realtime connections reconnect
    // on their own with a backoff, but after the page has been hidden for a while (computer
    // asleep, tab backgrounded) we might be deep into that backoff, so force them now.
    useOnPageBecomeActive(() => client.reconnect());

    return (
        <HiveContext.Provider value={client}>
            <HiveGlobalPresenceContext.Provider value={presenceValue}>
                {children}
            </HiveGlobalPresenceContext.Provider>
        </HiveContext.Provider>
    );
}
